import { useEffect, useState } from "react";
import {
  FirewallRecord,
  PaginatedListFirewallRecord,
} from "../../../services/swagger";
import { FirewallService } from "../../../services/api";
import CreateRecordDialog from "../../organisms/CreateRecordDialog";
import { useTranslation } from "react-i18next";
import DataView, { QueryParameters } from "../../organisms/DataView";
import ItemCard from "../../organisms/ItemCard";
import EditRecordDialog from "../../organisms/EditRecordDialog";
import { useNavigate, useParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import FormTemplate from "../../templates/FormTemplate";

export default function SecuritySettingsPage() {
  const { t } = useTranslation("SecurityPage");
  const { _id } = useParams();
  const navigate = useNavigate();
  const [createDialog, setCreateDialog] = useState(false);
  const [openedRecord, setOpenedRecord] = useState<
    FirewallRecord | undefined
  >();
  const [query, setQuery] = useState<QueryParameters>();
  const queryClient = useQueryClient();

  const loadRecords = async (): Promise<PaginatedListFirewallRecord | null> => {
    if (!query) return null;
    const res = await FirewallService.getRecordList({
      search: query.search,
      limit: query.limit,
      skip: query.skip,
      filters: query.filters,
      sortDirection: query.sortDirection,
      sortField: query.sortField,
    });
    if (res?.data?.data) return res.data.data;
    return null;
  };

  const { data: records, isPlaceholderData: isLoading } = useQuery({
    queryFn: loadRecords,
    queryKey: ["records", { query }],
    refetchInterval: 1000 * 10,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    maxPages: 10,
  });

  const loadRecord = async () => {
    if (!_id) return;
    const res = await FirewallService.getRecord({ id: _id });
    if (res?.data?.data) setOpenedRecord(res.data.data);
  };

  const handleDataTableChange = (query: QueryParameters) => {
    setQuery(query);
  };

  const handleCloseRecord = () => {
    if (_id) navigate("../");
    setOpenedRecord(undefined);
    queryClient.invalidateQueries({ queryKey: ["records"] });
  };

  const handleCloseCreateDialog = () => {
    setCreateDialog(false);
    queryClient.invalidateQueries({ queryKey: ["records"] });
  };

  useEffect(() => {
    if (_id) loadRecord();
  }, [_id]);

  return (
    <FormTemplate
      title={t("FirewallRecords")}
      disableFooter
      onCancel={() => navigate(-1)}
    >
      <CreateRecordDialog
        open={createDialog}
        onClose={handleCloseCreateDialog}
      />
      <EditRecordDialog record={openedRecord} onClose={handleCloseRecord} />
      <DataView
        name={t("FirewallRecords")}
        loading={isLoading || !records}
        onItemClick={(item) => setOpenedRecord(item)}
        headers={[
          { field: "date", name: t("Date"), type: "date" },
          { field: "ip", name: t("IP"), type: "string" },
          { field: "prefix", name: t("Prefix"), type: "number" },
          { field: "type", name: t("Type"), type: "string" },
        ]}
        onChange={handleDataTableChange}
        items={records?.docs || []}
        count={records?.count || 0}
        renderItem={(record) => <ItemCard record={record} />}
        pagination
        queryFunction={FirewallService.getRecordListQuery}
        button={{
          text: t("NewRecord"),
          onClick: () => setCreateDialog(true),
        }}
      />
    </FormTemplate>
  );
}
